/* fc-ai-chat.js (2026-10-18): FoldifyAI chat body, embedded in the support
   hub (sections/fc-floating-support.liquid → Chat tab).
   Markup: snippets/fc-ai-chat.liquid (root #fc-ai-chat with a JSON config in
   [data-fc-chat-cfg]). Backend: support-chat/ (Vercel), wire protocol in its
   README. The transcript and the signed session token live in sessionStorage
   so a conversation survives page navigation within the tab.
   The hub (assets/fc-floating-support.js) owns opening, closing and the
   tabs; it dispatches `fc:support:tab` {tab, open} on document whenever a
   tab is shown, and this script renders the transcript on the first show
   and focuses the composer on wide screens. window.fcAiChat.open() /
   close() / toggle() / isOpen() stay as thin wrappers over
   window.fcSupport for older callers.

   Product cards (2026-10-18, owner: "it just gives all text, no product
   image or link suggestion"): the backend's show_products tool streams a
   `products` event {items:[{handle, name, title, url, image, reason,
   variant_id, model, price_cents, compare_cents, price_from, available}],
   model}. Each item becomes a card (photo, name, the assistant's one-line
   reason, price, model, "View" link to the product page on that model's
   variant). The backend prices are the store's base currency, so the card
   re-reads price, stock and image from the storefront's own
   /products/<handle>.js (the shopper's market currency) and formats them with
   window.fcMoney (snippets/fc-money.liquid). Pre-order mirrors fc-pp-info:
   inside the window a variant whose option contains the match string shows
   the discounted price (price − floor(price × pct / 100)) with a tag, unless
   it is already on sale. Cards are kept in the saved transcript with the text
   position they appeared at, so a reload shows the same sequence. */
(function () {
  'use strict';
  function initChat(root) {
    var cfg = {};
    try { cfg = JSON.parse((root.querySelector('[data-fc-chat-cfg]') || {}).textContent || '{}'); } catch (e) { cfg = {}; }
    var T = cfg.t || {};
    var ENDPOINT = cfg.endpoint || '';
    var ROOT = String(cfg.root || '/').replace(/\/+$/, ''); // '' or '/de' (the harness passes its mock origin)
    var KEY_LOG = 'fcChatLog', KEY_SESSION = 'fcChatSession';
    var MAX_SENT = 16, MAX_CARDS = 4;
    var LINK_HOSTS = /(^|\.)foldifycase\.(com|com\.au|uk|de)$/i;
    var CDN_IMG = /^https:\/\/cdn\.shopify\.com\//i;
    var HANDLE_RE = /^[a-z0-9-]{1,120}$/;

    var log     = root.querySelector('[data-fc-chat-log]');
    var quick   = root.querySelector('[data-fc-chat-quick]');
    var form    = root.querySelector('[data-fc-chat-form]');
    var input   = root.querySelector('[data-fc-chat-input]');
    var sendBtn = root.querySelector('[data-fc-chat-send]');
    if (!log || !form || !input) return;

    var messages = [];          // [{role, content, cards?}] — cards: [{items, model, at}]
    var session = '';
    var busy = false;
    var rendered = false;
    var productCache = {};      // handle → Promise<product json | null>

    /* ── storage ── */
    function load() {
      try {
        var saved = JSON.parse(sessionStorage.getItem(KEY_LOG) || '[]');
        if (Array.isArray(saved)) {
          messages = saved.filter(function (m) { return m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string'; }).slice(-40);
          messages.forEach(function (m) { if (m.cards && !Array.isArray(m.cards)) delete m.cards; });
        }
        session = sessionStorage.getItem(KEY_SESSION) || '';
      } catch (e) { messages = []; session = ''; }
    }
    function save() {
      try {
        sessionStorage.setItem(KEY_LOG, JSON.stringify(messages.slice(-40)));
        if (session) sessionStorage.setItem(KEY_SESSION, session); else sessionStorage.removeItem(KEY_SESSION);
      } catch (e) { /* private mode */ }
    }
    function history() {
      return messages.slice(-MAX_SENT).map(function (m) { return { role: m.role, content: m.content }; });
    }

    /* ── rendering ── */
    function esc(s) {
      return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
    }
    function safeHref(url) {
      url = String(url || '').trim();
      if (/^\/(?!\/)/.test(url)) return url;                       // store-relative path
      if (/^mailto:[^\s@]+@foldifycase\.com$/i.test(url)) return url;
      try {
        var u = new URL(url);
        if (u.protocol === 'https:' && LINK_HOSTS.test(u.hostname)) return u.href;
      } catch (e) { /* not a URL */ }
      return '';
    }
    // Tiny markdown: **bold**, [text](url) on store links only, "- " bullets, paragraphs.
    function inline(s) {
      var out = esc(s);
      out = out.replace(/\[([^\]]{1,120})\]\(([^)\s]{1,300})\)/g, function (m, text, url) {
        var href = safeHref(url.replace(/&amp;/g, '&'));
        return href ? '<a href="' + esc(href) + '"' + (/^https?:/i.test(href) ? ' target="_blank" rel="noopener"' : '') + '>' + text + '</a>' : text;
      });
      out = out.replace(/\*\*([^*]{1,200})\*\*/g, '<strong>$1</strong>');
      return out;
    }
    function renderMarkdown(text) {
      var lines = String(text || '').replace(/\r/g, '').split('\n');
      var html = '', para = [], list = [];
      function flushPara() { if (para.length) { html += '<p>' + para.join('<br>') + '</p>'; para = []; } }
      function flushList() { if (list.length) { html += '<ul>' + list.map(function (l) { return '<li>' + l + '</li>'; }).join('') + '</ul>'; list = []; } }
      lines.forEach(function (raw) {
        var line = raw.replace(/\s+$/, '');
        var m = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
        if (m) { flushPara(); list.push(inline(m[1])); return; }
        if (!line.trim()) { flushPara(); flushList(); return; }
        flushList();
        para.push(inline(line.replace(/^#{1,4}\s+/, '')));
      });
      flushPara(); flushList();
      return html || '<p></p>';
    }
    function scrollDown() { log.scrollTop = log.scrollHeight; }
    function addBubble(role, text) {
      var el = document.createElement('div');
      el.className = 'fc-chat__msg fc-chat__msg--' + role;
      if (role === 'user') el.textContent = text; else el.innerHTML = renderMarkdown(text);
      log.appendChild(el);
      scrollDown();
      return el;
    }
    function addNote(text, kind) {
      var el = document.createElement('div');
      el.className = 'fc-chat__note' + (kind ? ' fc-chat__note--' + kind : '');
      el.textContent = text;
      log.appendChild(el);
      scrollDown();
      return el;
    }
    var statusEl = null;
    function setStatus(text) {
      if (!statusEl) {
        statusEl = document.createElement('div');
        statusEl.className = 'fc-chat__status';
        statusEl.innerHTML = '<span class="fc-chat__dots" aria-hidden="true"><i></i><i></i><i></i></span><span data-fc-status-text></span>';
      }
      log.appendChild(statusEl); // always last
      statusEl.querySelector('[data-fc-status-text]').textContent = text || '';
      scrollDown();
    }
    function clearStatus() { if (statusEl) { statusEl.remove(); statusEl = null; } }

    /* ── product cards ── */
    function money(cents) {
      cents = Math.round(Number(cents) || 0);
      if (window.fcMoney) { try { return window.fcMoney(cents); } catch (e) { /* fall through */ } }
      try { return new Intl.NumberFormat(cfg.locale || 'en', { style: 'currency', currency: cfg.currency || 'AUD' }).format(cents / 100); } catch (e) { return (cents / 100).toFixed(2); }
    }
    function preorderActive() {
      var p = cfg.preorder || {};
      if (!p.on || !p.match) return false;
      var end = Date.parse(String(p.end || '') + 'T23:59:59+11:00'); // the theme's AEDT deadline
      return isNaN(end) ? true : Date.now() <= end;
    }
    function preorderFor(optionValues, price, compare) {
      if (!preorderActive()) return false;
      if (compare && compare > price) return false; // a sale and the pre-order discount never stack (fc-pp-info)
      var m = String(cfg.preorder.match).toLowerCase();
      return (optionValues || []).some(function (o) { return String(o || '').toLowerCase().indexOf(m) >= 0; });
    }
    function discounted(cents, pct) { pct = Number(pct) || 0; return cents - Math.floor(cents * pct / 100); }
    function cardHref(item) {
      var vid = parseInt(item.variant_id, 10);
      return ROOT + '/products/' + item.handle + (vid > 0 ? '?variant=' + vid : '');
    }
    function imgSrc(src) {
      var s = String(src || '').trim();
      if (!s) return '';
      if (!CDN_IMG.test(s) && !(/^https?:/i.test(ROOT) && s.indexOf(ROOT + '/') === 0)) return '';
      return s + (s.indexOf('?') >= 0 ? '&' : '?') + 'width=160';
    }
    // The price line: `live` is the variant from /products/<handle>.js when it has loaded
    // (prices in the shopper's currency); until then the backend's base-currency price is
    // shown only when that is the shopper's currency too.
    function metaHtml(item, live, from) {
      var price = null, compare = null, available = true, pre = false;
      if (live) {
        price = live.price; compare = live.compare_at_price || null; available = !!live.available;
        pre = preorderFor(live.options || [live.title], price, compare);
      } else if (!cfg.currency || cfg.currency === 'AUD') {
        price = item.price_cents; compare = item.compare_cents || null; available = item.available !== false; from = !!item.price_from;
        pre = preorderFor([item.model || ''], price, compare);
      }
      var parts = [];
      if (price != null && price > 0) {
        if (pre) {
          parts.push('<span class="fc-chat__card-price">' + esc(money(discounted(price, cfg.preorder.pct))) + '</span><s class="fc-chat__card-was">' + esc(money(price)) + '</s><span class="fc-chat__card-tag fc-chat__card-tag--pre">' + esc(((cfg.preorder || {}).kind || 'launch') === 'preorder' ? (T.preorder || 'Pre-order') : (T.launch || 'Launch deal')) + '</span>');
        } else {
          parts.push('<span class="fc-chat__card-price">' + (from ? esc(T.from || 'From') + ' ' : '') + esc(money(price)) + '</span>' + (compare && compare > price ? '<s class="fc-chat__card-was">' + esc(money(compare)) + '</s>' : ''));
        }
      }
      if (item.model) parts.push('<span class="fc-chat__card-model">' + esc(item.model) + '</span>');
      if (!available) parts.push('<span class="fc-chat__card-tag fc-chat__card-tag--out">' + esc(T.soldOut || 'Sold out') + '</span>');
      return { html: parts.join(''), available: available };
    }
    function cardEl(item) {
      var a = document.createElement('a');
      a.className = 'fc-chat__card';
      a.href = cardHref(item);
      a.setAttribute('role', 'listitem');
      a.setAttribute('data-fc-card', item.handle);
      var img = imgSrc(item.image);
      var name = item.name || item.title || item.handle;
      var meta = metaHtml(item, null, !!item.price_from);
      a.innerHTML =
        '<span class="fc-chat__card-img" aria-hidden="true">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="72" height="72" data-fc-card-img>' : '') + '</span>' +
        '<span class="fc-chat__card-body"><strong class="fc-chat__card-name">' + esc(name) + '</strong>' +
        (item.reason ? '<span class="fc-chat__card-reason">' + esc(item.reason) + '</span>' : '') +
        '<span class="fc-chat__card-meta" data-fc-card-meta>' + meta.html + '</span></span>' +
        '<span class="fc-chat__card-cta" aria-hidden="true">' + esc(T.view || 'View') + '</span>';
      a.setAttribute('aria-label', name + (item.model ? ' · ' + item.model : ''));
      if (!meta.available) a.classList.add('fc-chat__card--out');
      return a;
    }
    function productJson(handle) {
      if (!productCache[handle]) {
        productCache[handle] = fetch(ROOT + '/products/' + handle + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      return productCache[handle];
    }
    // Fill price, stock and image from the storefront (market currency, live stock).
    function hydrate(a, item) {
      productJson(item.handle).then(function (j) {
        if (!j || !Array.isArray(j.variants) || !j.variants.length) return;
        var vid = parseInt(item.variant_id, 10), v = null, from = false;
        if (vid > 0) v = j.variants.filter(function (x) { return x.id === vid; })[0] || null;
        if (!v && item.model) {
          var same = j.variants.filter(function (x) { return (x.options || []).indexOf(item.model) >= 0; });
          v = same.filter(function (x) { return x.available; })[0] || same[0] || null;
        }
        if (!v) {
          var sorted = j.variants.slice().sort(function (x, y) { return x.price - y.price; });
          v = sorted.filter(function (x) { return x.available; })[0] || sorted[0];
          from = j.variants.length > 1 && j.price_min !== j.price_max;
        }
        var meta = metaHtml(item, v, from);
        var metaEl = a.querySelector('[data-fc-card-meta]');
        if (metaEl) metaEl.innerHTML = meta.html;
        a.classList.toggle('fc-chat__card--out', !meta.available);
        var wrap = a.querySelector('.fc-chat__card-img');
        if (wrap && !wrap.querySelector('img')) {
          var src = imgSrc((v.featured_image && v.featured_image.src) || j.featured_image || (j.images && j.images[0]));
          if (src) wrap.innerHTML = '<img src="' + esc(src) + '" alt="" loading="lazy" width="72" height="72" data-fc-card-img>';
        }
      });
    }
    function validCard(it) { return !!(it && typeof it === 'object' && HANDLE_RE.test(String(it.handle || ''))); }
    function addCards(payload) {
      var items = (payload && Array.isArray(payload.items) ? payload.items : []).filter(validCard).slice(0, MAX_CARDS);
      if (!items.length) return null;
      var wrap = document.createElement('div');
      wrap.className = 'fc-chat__cards';
      wrap.setAttribute('role', 'list');
      items.forEach(function (item) {
        var a = cardEl(item);
        wrap.appendChild(a);
        hydrate(a, item);
      });
      log.appendChild(wrap);
      scrollDown();
      return wrap;
    }
    // A saved assistant turn: text segments and card blocks in the order they arrived.
    function renderAssistant(m) {
      var text = String(m.content || '');
      var cards = Array.isArray(m.cards) ? m.cards.slice().sort(function (a, b) { return (a.at || 0) - (b.at || 0); }) : [];
      var pos = 0;
      cards.forEach(function (c) {
        var at = Math.min(Math.max(Number(c.at) || 0, pos), text.length);
        var seg = text.slice(pos, at);
        if (seg.trim()) addBubble('assistant', seg);
        addCards(c);
        pos = at;
      });
      var rest = text.slice(pos);
      if (rest.trim() || !cards.length) addBubble('assistant', rest);
    }
    function renderAll() {
      log.innerHTML = '';
      statusEl = null;
      if (!messages.length) addBubble('assistant', cfg.greeting || '');
      messages.forEach(function (m) { if (m.role === 'assistant') renderAssistant(m); else addBubble('user', m.content); });
      if (quick) quick.hidden = messages.length > 0;
      scrollDown();
    }
    function ensureRendered() {
      if (rendered) return;
      rendered = true;
      load();
      renderAll();
    }
    function wideScreen() {
      try { return window.matchMedia('(min-width: 600px)').matches; } catch (e) { return true; }
    }
    // Shown by the hub: render once, scroll to the end, focus the composer on wide screens
    // (on phones the keyboard would cover half the transcript).
    function shown() {
      ensureRendered();
      setTimeout(function () {
        scrollDown();
        if (wideScreen()) { try { input.focus({ preventScroll: true }); } catch (e) {} }
      }, 120);
    }
    function isVisible() { return !!(root.offsetParent || root.getClientRects().length); }
    document.addEventListener('fc:support:tab', function (e) {
      var d = (e && e.detail) || {};
      if (d.tab === 'chat' && d.open !== false) shown();
    });
    if (isVisible()) ensureRendered();

    window.fcAiChat = {
      open: function () { if (window.fcSupport) window.fcSupport.open('chat'); },
      close: function () { if (window.fcSupport) window.fcSupport.close(); },
      toggle: function () { if (window.fcSupport) { if (window.fcSupport.isOpen()) window.fcSupport.close(); else window.fcSupport.open('chat'); } },
      isOpen: function () { return !!(window.fcSupport && window.fcSupport.isOpen()); }
    };
    root.querySelectorAll('[data-fc-chat-new]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (busy) return;
        messages = []; session = ''; save(); rendered = true; renderAll(); input.value = ''; autosize();
        if (wideScreen()) { try { input.focus({ preventScroll: true }); } catch (e) {} }
      });
    });

    /* ── composer ── */
    function autosize() {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 120) + 'px';
    }
    input.addEventListener('input', autosize);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); if (form.requestSubmit) form.requestSubmit(); else submit(); }
    });
    form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    if (quick) {
      quick.querySelectorAll('[data-fc-chat-chip]').forEach(function (chip) {
        chip.addEventListener('click', function () { send(chip.textContent.trim()); });
      });
    }
    function submit() {
      var text = input.value.replace(/\s+$/, '');
      if (!text.trim()) return;
      input.value = ''; autosize();
      send(text.trim());
    }
    function setBusy(on) {
      busy = on;
      if (sendBtn) sendBtn.disabled = on;
      input.disabled = on;
    }

    /* ── talking to the backend ── */
    function ctx() {
      return {
        locale: cfg.locale, country: cfg.country, currency: cfg.currency,
        url: location.pathname, page: cfg.page || {}, preorder: cfg.preorder || {}
      };
    }
    function toolLabel(name) {
      if (name === 'lookup_order' || name === 'track_shipment') return T.toolOrder || '';
      if (name === 'search_products' || name === 'get_product') return T.toolCatalog || '';
      if (name === 'show_products') return T.toolCards || T.toolCatalog || '';
      if (name === 'request_human_followup') return T.toolHandoff || '';
      return T.thinking || '';
    }
    function send(text) {
      if (busy || !text) return;
      ensureRendered();
      if (!ENDPOINT) { addNote(T.offline || 'Offline', 'err'); return; }
      messages.push({ role: 'user', content: text });
      save();
      addBubble('user', text);
      if (quick) quick.hidden = true;
      setBusy(true);
      setStatus(T.thinking || '');
      var bubble = null, answer = '', segStart = 0, cards = [], gotDone = false;
      var payload = { messages: history(), session: session || undefined, ctx: ctx() };

      function onEvent(ev) {
        if (!ev || typeof ev !== 'object') return;
        if (ev.type === 'token') {
          clearStatus();
          answer += ev.t || '';
          var seg = answer.slice(segStart);
          if (!seg.trim()) return;
          if (!bubble) bubble = addBubble('assistant', seg); else { bubble.innerHTML = renderMarkdown(seg); scrollDown(); }
        } else if (ev.type === 'products') {
          clearStatus();
          var items = (Array.isArray(ev.items) ? ev.items : []).filter(validCard).slice(0, MAX_CARDS);
          if (!items.length) return;
          var block = { items: items, model: typeof ev.model === 'string' ? ev.model : null, at: answer.length };
          cards.push(block);
          addCards(block);
          if (bubble) { bubble = null; segStart = answer.length; } // the next words start a new bubble under the cards
        } else if (ev.type === 'tool') {
          setStatus(toolLabel(ev.name));
        } else if (ev.type === 'handoff') {
          onHandoff(ev);
        } else if (ev.type === 'done') {
          gotDone = true;
          if (ev.session) session = ev.session;
        } else if (ev.type === 'error') {
          clearStatus();
          addNote(ev.message || T.error || 'Error', 'err');
        }
      }
      function finish() {
        clearStatus();
        if (answer.trim() || cards.length) {
          var m = { role: 'assistant', content: answer };
          if (cards.length) m.cards = cards;
          messages.push(m);
        } else if (!gotDone && !log.querySelector('.fc-chat__note--err:last-child')) { addNote(T.error || 'Error', 'err'); }
        save();
        setBusy(false);
        if (wideScreen()) { try { input.focus({ preventScroll: true }); } catch (e) {} }
      }

      fetch(ENDPOINT, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (j) {
              var msg = (j && j.error === 'session_limit') ? (T.limit || j.message) : (j && j.message) || T.error;
              clearStatus(); addNote(msg || 'Error', 'err');
              if (j && j.error === 'session_limit') { session = ''; }
              finish();
            });
          }
          var reader = res.body.getReader(), dec = new TextDecoder(), buf = '';
          function pump() {
            return reader.read().then(function (r) {
              if (r.done) { flush(buf); finish(); return; }
              buf += dec.decode(r.value, { stream: true });
              var idx;
              while ((idx = buf.indexOf('\n\n')) >= 0) { flush(buf.slice(0, idx)); buf = buf.slice(idx + 2); }
              return pump();
            });
          }
          function flush(chunk) {
            chunk.split('\n').forEach(function (line) {
              if (line.indexOf('data: ') !== 0) return;
              try { onEvent(JSON.parse(line.slice(6))); } catch (e) { /* skip bad line */ }
            });
          }
          return pump();
        })
        .catch(function () { clearStatus(); addNote(T.error || 'Error', 'err'); finish(); });
    }

    /* ── hand-off: the backend has emailed the team (summary + transcript); Shopify's
       own contact form refuses programmatic posts (reCAPTCHA), so nothing is posted
       from here. delivered:false means the mail transport is missing or failed. ── */
    function onHandoff(h) {
      if (h && h.delivered) addNote(T.handoffOk || 'Sent.', 'ok');
      else addNote(T.handoffFail || 'Could not send.', 'err');
    }
  }

  function init() {
    var root = document.getElementById('fc-ai-chat');
    if (!root || root.getAttribute('data-fc-init') === '1') return;
    root.setAttribute('data-fc-init', '1');
    initChat(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#fc-ai-chat')) init();
  });
})();
